import { UserFilterPreset } from '@akebono/core/lib/graphql/service/graphql-currency-exchange-service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { combineLatest, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { DateRange, getDaysRange, simpleNameSort, toLocalIsoString } from 'src/app/const';
import {
  LotsListFiltersGQL,
  LotsListFiltersQuery,
  LotStatusEnum,
} from 'src/app/modules/graphql/service/graphql-cars-default-service';

import { AutoLotsFilterGuideService } from '../../guides/auto-lots-filter-guide/services/auto-lots-filter-guide.service';
import { frameKey, frameVariants, normalizeFrame } from 'src/app/utils/frame.util';

@Component({
  selector: 'app-auto-lots-filter',
  templateUrl: './auto-lots-filter.component.html',
  styleUrls: ['./auto-lots-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutoLotsFilterComponent implements OnDestroy {
  data: any = {
    total: 0,
    nodes: [],
  };
  filtersData: LotsListFiltersQuery['lotsFiltersData'];
  frameOptions: LotsListFiltersQuery['lotsFiltersData']['modelTypeEnOptions'] = [];
  form: FormGroup;

  loadingFilters = true;
  loadingFrames = true;
  extendedSearchVisible = false;

  page = 1;
  pageSize = 20;

  years: number[] = [];
  minYear = 1985;

  modelsList: any[];
  otherCompaniesList: any[];
  mainCompaniesList: any[];
  mainCompaniesNames = [
    'BMW',
    'MERCEDES BENZ',
    'DAIHATSU',
    'MAZDA',
    'SUZUKI',
    'MITSUBISHI',
    'SUBARU',
    'HONDA',
    'NISSAN',
    'TOYOTA',
  ];
  auctRefOptions: any[] = [];
  modelGradeEnOptions: any[] = [];
  trucks: any[];
  auctions: any[];
  isYearFiltered = false;
  isMileageFiltered = false;
  isEngineVolumeFiltered = false;
  isActiveFilters = true;
  lastModelId: string;
  weekdayTranslationKeys = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  inclusionFilterFields = {
    frame: 'modelTypeEn',
    score: 'scoresEn',
    auction: 'auctRef',
    color: 'colorEn',
    transmission: 'transmissionEn',
    date: 'date',
    result: 'result',
  };
  lastDay: Date;
  dateRange: DateRange[] = [];
  dumpVersion: string;
  outdated = false;
  presetContent: any = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private autoLotsFilterGuideService: AutoLotsFilterGuideService,
    private listLotsFiltersGQL: LotsListFiltersGQL,
  ) {
    this.initYears();
    this.initForm();
    this.patchFormValues();
    this.initCompanyNModelChangesSub();
    this.initDates();
    this.initPresetContent();
    this.initQueryParamsSub();
    this.initModelGradeSelectState();
  }

  initCompanyNModelChangesSub(): void {
    this.form
      .get('company')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(this.onSelectedCompanyChange.bind(this));
    this.form
      .get('model')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(this.onSelectedModelChange.bind(this));
  }

  initPresetContent(): void {
    this.presetContent = this.form.value;
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((content) => (this.presetContent = content));
  }

  initYears(): void {
    for (let year = new Date().getFullYear(); year >= this.minYear; year--) {
      this.years.push(year);
    }
  }

  initQueryParamsSub(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(async (params) => {
      await this.fetchFilterData(params);
    });
  }

  initModelGradeSelectState(): void {
    const { company, model, modelGrade } = this.form.controls;

    combineLatest([
      company.valueChanges.pipe(startWith(company.value)),
      model.valueChanges.pipe(startWith(model.value)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([company, model]) => {
        modelGrade.setValue(null);

        if (company && model && company !== 'any' && model !== 'any') {
          modelGrade.enable();
        } else {
          modelGrade.disable();
        }
      });
  }

  initForm(): void {
    this.form = this.fb.group({
      company: this.fb.control(null),
      model: this.fb.control(null),
      truck: this.fb.control(null),
      bid: this.fb.control(null),
      yearFrom: this.fb.control(null),
      yearTo: this.fb.control(null),
      mileageFrom: this.fb.control(null),
      mileageTo: this.fb.control(null),
      engineVolumeFrom: this.fb.control(null),
      engineVolumeTo: this.fb.control(null),
      inclusionFrame: this.fb.control([]),
      inclusionScore: this.fb.control(null),
      inclusionAuction: this.fb.control([]),
      inclusionColor: this.fb.control(null),
      inclusionStatus: this.fb.control(null),
      inclusionTransmission: this.fb.control(null),
      productionDate: this.fb.control(null),
      datetime: this.fb.control(null),
      hasBids: this.fb.control(null),
      hasNegotiations: this.fb.control(null),
      hasTranslations: this.fb.control(null),
      selectedDays: this.fb.control(null),
      modelGrade: this.fb.control(null),
      drive: this.fb.control(null),
    });
  }

  patchFormValues(): void {
    const formState = this.getFilterFormState(this.route.snapshot.queryParams);

    this.form.patchValue(
      {
        company: formState.company,
        model: formState.model,
        truck: formState.truck,
        bid: formState.bid,
        yearFrom: formState.yearFrom,
        yearTo: formState.yearTo,
        mileageFrom: formState.mileageFrom,
        mileageTo: formState.mileageTo,
        engineVolumeFrom: formState.engineVolumeFrom,
        engineVolumeTo: formState.engineVolumeTo,
        inclusionFrame: [formState.inclusionFrame].flat().map(normalizeFrame),
        inclusionScore: formState.inclusionScore,
        inclusionAuction: [formState.inclusionAuction].flat(),
        inclusionColor: formState.inclusionColor,
        inclusionStatus: formState.inclusionStatus,
        inclusionTransmission: formState.inclusionTransmission,
        productionDate: formState.productionDate,
        datetime: formState.datetime,
        hasBids: formState.hasBids,
        hasNegotiations: formState.hasNegotiations,
        hasTranslations: formState.hasTranslations,
        selectedDays: formState.selectedDays,
        modelGrade: formState.modelGrade,
        drive: formState.drive,
      },
      { emitEvent: false },
    );
  }

  async fetchFilterData(params: Params): Promise<void> {
    this.loadingFrames = true;

    this.lastModelId = params.model;
    const hasModelId = !!(params.model && params.model !== 'any');
    const filter = this.getQueryFilter(params);

    const result = await this.listLotsFiltersGQL
      .fetch(
        {
          withModel: hasModelId,
          filter,
        },
        {
          fetchPolicy: 'network-only',
        },
      )
      .toPromise();

    this.checkOutdated(result.data.dumpVersion);

    const domParser = new DOMParser();

    this.auctRefOptions = result.data.lotsFiltersData.auctRefOptions
      .map((op) => op.autoAuction)
      .sort(simpleNameSort);
    this.modelGradeEnOptions = result.data.lotsFiltersData.modelGradeEnOptions
      .map((op) => op.modelGradeEn)
      .filter((op) => !!op.trim())
      .sort(simpleNameSort)
      .map((value) => ({
        value,
        label: domParser.parseFromString(value, 'text/html').body.innerText,
      }));
    const frames = new Map<string, { value: string; count: number }>();

    for (const option of result.data.lotsFiltersData.modelTypeEnOptions ?? []) {
      if (!option?.value) {
        continue;
      }

      const key = frameKey(option.value);
      const frame = frames.get(key);

      if (frame) {
        frame.count += option.count ?? 0;
      } else {
        frames.set(key, {
          value: normalizeFrame(option.value),
          count: option.count ?? 0,
        });
      }
    }

    this.frameOptions = [...frames.values()];
    this.otherCompaniesList = result.data.autoCompanies.nodes
      .filter((c) => !this.mainCompaniesNames.includes(c.name.toLocaleUpperCase()))
      .sort(simpleNameSort);
    this.mainCompaniesList = result.data.autoCompanies.nodes
      .filter((c) => this.mainCompaniesNames.includes(c.name.toLocaleUpperCase()))
      .sort(simpleNameSort)
      .reverse();

    if (!this.auctions) {
      this.auctions = result.data.autoAuctions.nodes;
    }
    if (!this.trucks) {
      this.trucks = result.data.autoTrucks.nodes;
    }
    if (!this.modelsList) {
      this.modelsList = result.data.autoModels.nodes;
    }

    this.filtersData = result.data.lotsFiltersData;
    this.patchFormValues();
    this.cdr.markForCheck();
    this.loadingFilters = false;
    this.loadingFrames = false;
  }

  getQueryFilter(params: any) {
    const inclusionFilter = [];

    Object.keys(this.inclusionFilterFields).forEach((key) => {
      if (params[key] && params[key].length > 0) {
        inclusionFilter.push({
          fieldName: this.inclusionFilterFields[key],
          eq:
            key === 'frame'
              ? [...new Set([].concat(params[key]).flatMap(frameVariants))]
              : [].concat(params[key]),
        });
      }
    });

    const mileageFrom = Number(params.mileageFrom);
    const mileageTo = Number(params.mileageTo);

    return {
      companyRef: {
        eq: params.company,
      },
      modelNameRef: {
        eq: params.model,
      },
      truckRef: {
        eq: params.truck,
      },
      year: {
        gte: Number(params.yearFrom),
        lte: Number(params.yearTo),
      },
      mileage: {
        gte: mileageFrom ? mileageFrom : null,
        lte: mileageTo ? mileageTo : null,
      },
      engineVolume: {
        gte: Number(params.engineVolumeFrom),
        lte: Number(params.engineVolumeTo),
      },
      productionDate: {
        gte: params.productionDateFrom,
        lte: params.productionDateTo,
      },
      bid: {
        eq: params.bid,
      },
      inclusion:
        inclusionFilter.length > 0
          ? {
              columns: inclusionFilter,
            }
          : undefined,
      datetime: {
        gt: params.datetime,
      },
      bidsCount: params.hasBids === 'true' ? { gte: 1 } : undefined,
      savedLotStatus:
        params.hasNegotiations === 'true' ? { eq: LotStatusEnum.Negotiations } : undefined,
      translationsCount: params.hasTranslations === 'true' ? { gte: 1 } : undefined,
      leftDrive: {
        eq: params.drive ? params.drive === 'left' : undefined,
      },
    };
  }

  onSelectedCompanyChange(company: string): void {
    this.form.get('bid').setValue(null);
    this.form.get('inclusionScore').setValue(['any']);
    this.form.get('inclusionFrame').setValue(['any']);
    this.form.get('inclusionColor').setValue(['any']);
    this.form.get('inclusionTransmission').setValue(['any']);
    this.form.get('inclusionAuction').setValue(['any']);
    this.router.navigate(['auto'], {
      queryParams: {
        page: 1,
        company: company,
        model: 'any',
        frame: null,
        bid: null,
        modelGrade: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  onSelectedModelChange(model: string): void {
    this.form.get('bid').setValue(null);
    this.form.get('inclusionScore').setValue(['any']);
    this.form.get('inclusionFrame').setValue(['any']);
    this.form.get('inclusionColor').setValue(['any']);
    this.form.get('inclusionTransmission').setValue(['any']);
    this.form.get('inclusionAuction').setValue(['any']);
    this.router.navigate(['auto'], {
      queryParams: {
        page: 1,
        model: model,
        modelGrade: null,
        frame: null,
        color: null,
        auction: null,
        score: null,
        bid: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  private toUTCDate(date: Date): Date {
    date.setUTCDate(date?.getDate());
    return date;
  }

  filter(): void {
    const value = this.form.value;
    const bidSet = value.bid && value.bid !== '№ лота';

    this.router.navigate(['auto'], {
      queryParams: {
        r: Math.floor(Math.random() * 100),
        page: 1,
        company: value.company,
        model: value.model,
        truck: value.truck,
        bid: bidSet ? value.bid : undefined,
        yearFrom: value.yearFrom ? value.yearFrom : undefined,
        yearTo: value.yearTo ? value.yearTo : undefined,
        mileageFrom: value.mileageFrom ? value.mileageFrom : undefined,
        mileageTo: value.mileageTo ? value.mileageTo : undefined,
        engineVolumeFrom: value.engineVolumeFrom ? value.engineVolumeFrom : undefined,
        engineVolumeTo: value.engineVolumeTo ? value.engineVolumeTo : undefined,
        modelGrade: value.modelGrade ? value.modelGrade : undefined,
        drive: value.drive ? value.drive : undefined,
        productionDateFrom: value.productionDate[0]
          ? this.toUTCDate(value.productionDate[0]).toISOString()
          : undefined,
        productionDateTo: value.productionDate[1]
          ? this.toUTCDate(value.productionDate[1]).toISOString()
          : undefined,
        frame:
          typeof value.inclusionFrame !== 'string' && value.inclusionFrame.length > 0
            ? value.inclusionFrame.filter((v) => v !== 'any')
            : undefined,
        score:
          typeof value.inclusionScore !== 'string' && value.inclusionScore.length > 0
            ? value.inclusionScore.filter((v) => v !== 'any')
            : undefined,
        auction:
          typeof value.inclusionAuction !== 'string' && value.inclusionAuction.length > 0
            ? value.inclusionAuction.filter((v) => v !== 'any')
            : undefined,
        color:
          typeof value.inclusionColor !== 'string' && value.inclusionColor.length > 0
            ? value.inclusionColor.filter((v) => v !== 'any')
            : undefined,
        status: value.inclusionStatus.length > 0 ? value.inclusionStatus : undefined,
        transmission:
          typeof value.inclusionTransmission !== 'string' && value.inclusionTransmission.length > 0
            ? value.inclusionTransmission.filter((v) => v !== 'any')
            : undefined,
        date: value.selectedDays.length > 0 ? value.selectedDays : undefined,
        datetime: value.datetime
          ? DateTime.local().setZone('Asia/Tokyo').plus({ minutes: 15 }).toISO()
          : undefined,
        hasBids: value.hasBids ? value.hasBids : undefined,
        hasNegotiations: value.hasNegotiations ? value.hasNegotiations : undefined,
        hasTranslations: value.hasTranslations ? value.hasTranslations : undefined,
      },
      queryParamsHandling: 'merge',
    });
  }

  initDates(): void {
    this.lastDay = new Date();
    this.lastDay.setDate(this.lastDay.getDate() + 7);

    if (this.dateRange.length === 0) {
      const dates: Date[] = getDaysRange(new Date(), new Date(this.lastDay));

      dates.forEach((item) => {
        this.dateRange.push({
          date: item,
          checked: this.form.value.selectedDays.indexOf(toLocalIsoString(this.getDate(item))) > -1,
        });
      });

      this.dateRange.sort((a, b) => {
        if (a.date === b.date) {
          return 0;
        } else if ((a.date.getDay() + 6) % 7 > (b.date.getDay() + 6) % 7) {
          return 1;
        } else {
          return -1;
        }
      });
    } else {
      this.dateRange.forEach((item) => {
        item.checked =
          this.form.value.selectedDays.indexOf(toLocalIsoString(this.getDate(item.date))) > -1;
      });
    }
  }

  clearValue(formControlName: string): void {
    this.form.get(formControlName).setValue(null);
  }

  resetFilter(): boolean {
    this.form.reset(this.getFilterFormState({}));
    this.initDates();
    this.router.navigate(['auto']);
    this.outdated = false;

    return false;
  }

  selectAuctionDate(event, date: DateRange): void {
    const dateJp = toLocalIsoString(
      this.getDate(date.date.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' })),
    );

    date.checked = event;

    if (event) {
      this.form.controls.selectedDays.setValue(
        this.form.controls.selectedDays.value.concat(dateJp),
      );
    } else {
      const day = this.form.controls.selectedDays.value.indexOf(dateJp);

      if (day > -1) {
        const days = this.form.controls.selectedDays.value;
        days.splice(day, 1);
        this.form.controls.selectedDays.setValue(days);
      }
    }

    this.filter();
  }

  private checkOutdated(dumpVersion): void {
    if (!this.dumpVersion) {
      this.dumpVersion = dumpVersion;
    } else if (this.dumpVersion !== dumpVersion) {
      this.outdated = true;
    }
  }

  getDate(string = null): Date {
    if (string) {
      return new Date(string);
    }

    return new Date();
  }

  getFilterFormState(params: any) {
    this.isYearFiltered = (params.yearFrom || params.yearTo) as boolean;
    this.isMileageFiltered = (params.mileageFrom || params.mileageTo) as boolean;
    this.isEngineVolumeFiltered = (params.engineVolumeFrom || params.engineVolumeTo) as boolean;

    const productionDate = [];

    if (params.productionDateFrom) {
      productionDate[0] = params.productionDateFrom;
    }

    if (params.productionDateTo) {
      productionDate[1] = params.productionDateTo;
    }

    return {
      company: params.company || 'any',
      model: params.model || 'any',
      bid: params.bid || '№ лота',
      yearFrom: params.yearFrom ? Number(params.yearFrom) : null,
      yearTo: params.yearTo ? Number(params.yearTo) : null,
      mileageFrom: params.mileageFrom || null,
      mileageTo: params.mileageTo || null,
      engineVolumeFrom: params.engineVolumeFrom || null,
      engineVolumeTo: params.engineVolumeTo || null,
      truck: params.truck ? [].concat(params.truck) : undefined,
      productionDate,
      inclusionFrame: params.frame ? [].concat(params.frame) : ['any'],
      inclusionAuction: params.auction ? [].concat(params.auction) : ['any'],
      inclusionScore: params.score ? [].concat(params.score) : ['any'],
      inclusionColor: params.color ? [].concat(params.color) : ['any'],
      inclusionStatus: params.status ? [].concat(params.status) : [],
      inclusionTransmission: params.transmission ? [].concat(params.transmission) : ['any'],
      datetime: params.datetime || false,
      hasBids: params.hasBids || false,
      hasNegotiations: params.hasNegotiations || false,
      hasTranslations: params.hasTranslations || false,
      selectedDays: params.date ? [].concat(params.date) : [],
      modelGrade: params.modelGrade ? [].concat(params.modelGrade) : [],
      drive: params.drive || null,
    };
  }

  valueIncludes(control: AbstractControl, value: any) {
    return typeof control.value.includes === 'function' && control.value.includes(value);
  }

  addValueToControl(control: AbstractControl, value: any): void {
    const newValue = control.value.concat(value).filter((v) => v !== 'any');

    control.setValue(newValue);
  }

  removeValueFromControl(control: AbstractControl, value: any): void {
    control.setValue(control.value.filter((v: any) => v !== value));
  }

  applyFilterPreset(preset: UserFilterPreset): void {
    this.form.patchValue({
      ...preset.content,
      inclusionFrame: [preset.content.inclusionFrame ?? 'any'].flat().map(normalizeFrame),
    });
    this.initDates();
    this.filter();
  }

  startGuide(event: Event): void {
    if (this.isActiveFilters) {
      event.stopPropagation();
    }

    this.autoLotsFilterGuideService.start();
  }

  ngOnDestroy(): void {
    this.autoLotsFilterGuideService.forceClose();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
